<template>
  <net-item
    v-if="treeNode && data"
    class="scene-medium__container"
    item-class="scene-medium"
    :uid="nodeUid"
    :size="size"
    :node-uid="nodeUid"
    :class="{
      '-selected': selected,
      '-editable': editable,
    }"
    :min-width="20"
    :min-height="20 / size.width * size.height"
    :grid-size="1"
    :selected="selected"
    :aspect-ratio-fixed="treeNode.node.aspectRatioLocked"
    :clickable="clickable"
    :locked="treeNode.locked"
    :edges="{
      top: true,
      right: true,
      bottom: true,
      left: true
    }"
    @resize="onResize"
    @move="onMove"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @select="select($event)"
  >
    <div class="scene-medium__main" ref="dropzoneRef" :class="{ '-file-over': isOverDropZone }">
      <template v-if="data.mediaType && mediumData">
        <img
          v-if="data.mediaType?.startsWith('image')"
          class="scene-medium__image"
          draggable="false"
          :src="mediumData"
          :width="size.width"
          :height="size.height"
        />
        <video
          v-else-if="data.mediaType?.startsWith('video')"
          class="scene-medium__video"
          draggable="false"
          :src="mediumData"
          :width="size.width"
          :height="size.height"
          :loop="true"
          :muted="true"
          :autoplay="true"
        ></video>
        <iframe
          v-else-if="data.mediaType == 'application/pdf'"
          :src="mediumData"
          credentialless="true"
          referrerpolicy="no-referrer"
          sandbox="allow-forms"
          class="scene-medium__embedded"
        ></iframe>
      </template>
      <label v-else class="scene-medium__uploadbox">
        <lucide-icon name="image" class="scene-medium__uploadicon" />
        <input type="file" class="scene-medium__input" @change="onImageFileChange" />
      </label>

      <transition name="transition-fade">
        <div v-show="isOverDropZone" class="scene-medium__dropicon">
          <lucide-icon name="upload" />
        </div>
      </transition>
    </div>
  </net-item>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useDropZone } from "@vueuse/core";
import { fileToDataURL, getDataUrlDimensions, warnIfFileIsLarge } from "@/helpers/utility/File";

import NetItem from "@editor/net/NetItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useSceneTree } from "../scene/sceneTree";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSceneMediumStore } from "./sceneMedia.store";

import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { Uid } from "@/helpers/utility/Id";
import type { RotatableRect } from "../scene/SceneNodeItem";
import type { SceneTreeNode } from "../scene/SceneTreeItem";

const props = withDefaults(defineProps<{
  nodeUid: Uid<"sceneNode">;
  editable: boolean;
  size: RotatableRect;
  selection: CanvasSelection;
  clickable?: boolean;
}>(), {
  editable: false,
  clickable: true
});

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"sceneNode"> }): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "move", delta: { x: number; y: number; gridX: number; gridY: number }): void;
}>();

const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();
const sceneMediaStore = useSceneMediumStore();
const treeNode = computed(() => {
  return sceneTree.treeNodes.get(props.nodeUid) as SceneTreeNode<"medium"> | undefined;
});
const data = computed(() => {
  return treeNode.value?.node.data;
});
const selectionStore = useSelectionStore();

const dropzoneRef = ref<HTMLElement>();

const selected = computed(() => {
  return selectionStore.selectedItems.some((item) => item.type == "sceneNode" && item.id == treeNode.value?.id);
});

// ====================
// IMAGE

const mediumData = computed(() => {
  return sceneMediaStore.mediaData[props.nodeUid] ?? undefined;
});

const { isOverDropZone } = useDropZone(
  dropzoneRef,
  {
    onDrop: (files: File[] | null, event) => {
      event.stopPropagation();
      event.preventDefault();
      if (!files) {
        return;
      } else {
        overrideMedium(files[0]);
      }
    },
    dataTypes: ["image/svg+xml", "image/png", "image/jpeg", "image/gif", "video/mp4", "video/webm", "video/ogg", "image/*", "video/*"]
  }
);

async function overrideMedium(file: File | null) {
  if (!file) return;

  const type = file.type;
  
  if (!type.startsWith("image") && !type.startsWith("video")) {
    throw new Error("Only images and videos are allowed");
  }

  warnIfFileIsLarge(file, 4); // Warn if the file is larger than 4MB

  const dataURl = await fileToDataURL(file);
  const dimensions = await getDataUrlDimensions(dataURl, type);
  console.log("dimensions", dimensions);

  const aspectRatio = dimensions.width / dimensions.height;
  const newHeight = props.size.width / aspectRatio;

  const node = treeNode.value;
  if (!node) return;

  sceneNodeStore.update({
    id: node.id,
    height: newHeight,
    data: {
      ...node.node.data,
      data: dataURl,
      mediaType: file.type,
    }
  });
}

function select(item: { uid: Uid<"sceneNode"> }) {
  if (!props.editable) return;
  emit("select", item);
}

// ====================
// Drag and resize handlers

function onDragStart() {
  // Group the action to make sure the movement of all
  // targets can be undone at once
  useUndo().startGroup("model", "moveText");
}

function onDragEnd() {
  // Close the drag action undo group, including
  // the area update
  useUndo().endGroup("model");
}

function onResize(newSize: {
  x: number;
  y: number;
  height: number;
  width: number;
}) {
  const node = treeNode.value;
  if (!node) return;
  sceneNodeStore.update({
    id: node.id,
    x: newSize.x,
    y: newSize.y,
    height: newSize.height,
    width: newSize.width
  });
}
function onMove(delta: { x: number; y: number; gridX: number; gridY: number }) {
  emit("move", delta);
}

function onImageFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;

  overrideMedium(file);
}
</script>

<style lang="scss">
.scene-medium {
  display: flex;
  justify-content: stretch;
  align-items: stretch;

  &__container {
    pointer-events: none;

    &.-editable {
      pointer-events: auto;
    }
  }

  &__main {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__image {
    object-fit: fill;
  }

  &__image,
  &__video {
    user-select: none;
  }

  &__embedded {
    width: 100%;
    height: 100%;
  }

  &__uploadbox {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--color-action-light);
  }
  &__uploadicon {
    width: 2rem;
    height: 2rem;
  }
  &__input {
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;
    opacity: 0;
  }

  &.is-selected {
    outline: 2px solid var(--color-primary);
  }

  &__dropicon {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: hsla(var(--color-primary-h), 100%, 40%, 0.5);
    color: #fff;
  }
}
</style>
